import { Logo } from "./Logo";
import { Container } from "./Container";
import { landingContent } from "@/content/landing";

interface FooterLink {
  label: string;
  href: string;
  download?: boolean;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

const DEFAULT_COLUMNS: FooterColumn[] = landingContent.footer.columns;

interface FooterProps {
  className?: string;
  columns?: FooterColumn[];
  tagline?: string;
  copyright?: string;
}

export function Footer({
  className = "",
  columns,
  tagline,
  copyright,
}: FooterProps) {
  const resolvedColumns = columns ?? DEFAULT_COLUMNS;
  const resolvedTagline = tagline ?? landingContent.footer.tagline;
  const resolvedCopyright = copyright ?? landingContent.footer.copyright;

  return (
    <footer
      className={`border-t border-border-default w-full py-[48px] max-tablet:py-[24px] ${className}`}
    >
      <Container className="flex flex-col gap-[48px] items-start">
      {/* Main row: brand + link columns.
          desktop ≥1269 — brand left + 3 columns row;
          tablet/mobile <1269 — brand stacked above columns;
          mobile <769 — columns wrap to a 2-col grid */}
      <div className="flex gap-[64px] items-start w-full max-desktop:flex-col max-desktop:gap-[40px]">
        {/* Brand */}
        <div className="flex flex-col gap-[8px] items-start flex-1 min-w-0 max-w-[420px] max-desktop:max-w-none max-desktop:w-full">
          <Logo />
          <p
            className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text-subtle whitespace-nowrap max-tablet:whitespace-normal"
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          >
            {resolvedTagline}
          </p>
        </div>

        {/* Link columns */}
        <div className="flex flex-1 gap-[32px] items-start min-w-0 w-full max-tablet:grid max-tablet:grid-cols-2 max-tablet:gap-x-[16px] max-tablet:gap-y-[40px]">
          {resolvedColumns.map((col) => (
            <div key={col.title} className="flex flex-col gap-[20px] items-start flex-1 min-w-0 max-tablet:w-full max-tablet:last:col-span-2">
              <h4
                className="[font-family:var(--font-commissioner)] font-medium not-italic text-[18px] leading-[24px] text-text tracking-[-0.36px] w-full"
                style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
              >
                {col.title}
              </h4>
              <div className="flex flex-col gap-[16px] items-start w-full">
                {col.links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    download={link.download ? "" : undefined}
                    className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text-subtle hover:text-text transition-colors w-full"
                    style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Copyright */}
      <div className="flex gap-[4px] items-center shrink-0">
        <span
          className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          ©
        </span>
        <span
          className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text-subtle"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {resolvedCopyright}
        </span>
      </div>
      </Container>
    </footer>
  );
}
